export const ERROR_KIND = {
  OFFLINE: "offline",
  NETWORK: "network",
  TIMEOUT: "timeout",
  TEMPORARY: "temporary",
  NOT_FOUND: "not_found",
  ALREADY_UPLOADED: "already_uploaded",
  FILE_TOO_LARGE: "file_too_large",
  VALIDATION: "validation",
};

// Only backend codes and transport metadata determine guest-facing behavior.
const VALIDATION_CODES = new Set(["missing_file", "invalid_image", "missing_fields", "invalid_fields"]);

export function isBrowserOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function parseApiErrorPayload(data) {
  return {
    code: typeof data?.code === "string" ? data.code : "",
    field: typeof data?.field === "string" ? data.field : "",
  };
}

export function isAlreadyUploadedError(status, payload) {
  return parseApiErrorPayload(payload).code === "already_uploaded";
}

export function isNotFoundError(status, payload) {
  return parseApiErrorPayload(payload).code === "reservation_not_found" || status === 404;
}

export function canShowSuccessForError() {
  // ALWAYS_SHOW_SUCCESSFUL_SUBMIT means exactly that: the guest is never shown a
  // failure, whatever the transport or server did. The flag is checked by the
  // caller; this predicate exists so the intent is named and testable.
  return true;
}

export function classifyAxiosError(err) {
  if (!err) return ERROR_KIND.TEMPORARY;
  if (!err.response) {
    if (isBrowserOffline()) return ERROR_KIND.OFFLINE;
    if (["ECONNABORTED", "ETIMEDOUT"].includes(err.code)) return ERROR_KIND.TIMEOUT;
    return ERROR_KIND.NETWORK;
  }
  const { status, data } = err.response;
  const { code } = parseApiErrorPayload(data);
  if (status === 408 || status === 504) return ERROR_KIND.TIMEOUT;
  if (status === 413 || code === "file_too_large") return ERROR_KIND.FILE_TOO_LARGE;
  if (isAlreadyUploadedError(status, data)) return ERROR_KIND.ALREADY_UPLOADED;
  if (isNotFoundError(status, data)) return ERROR_KIND.NOT_FOUND;
  if (status === 429 || status >= 500) return ERROR_KIND.TEMPORARY;
  if (VALIDATION_CODES.has(code)) return ERROR_KIND.VALIDATION;
  return ERROR_KIND.TEMPORARY;
}

export function getLocalizedErrorCopy(kind, lang = "en") {
  const it = lang === "it";

  const copies = {
    [ERROR_KIND.OFFLINE]: {
      title: it ? "Sei offline" : "You're offline",
      body: it
        ? "Non riusciamo a collegarci finché il dispositivo non ha internet. Controlla Wi‑Fi o dati mobili e riprova."
        : "We can't connect while your device is offline. Check Wi‑Fi or mobile data, then try again.",
      short: it
        ? "Sei offline. Controlla la connessione e riprova."
        : "You're offline. Check your connection and try again.",
    },
    [ERROR_KIND.NETWORK]: {
      title: it ? "Riproviamo" : "Let's try again",
      body: it ? "Non riusciamo a confermare l'invio. Resta su questa pagina e riprova." : "We couldn't confirm delivery. Stay on this page and try again.",
      short: it ? "Non riusciamo a confermare l'invio. Riprova." : "We couldn't confirm delivery. Please try again.",
    },
    [ERROR_KIND.TIMEOUT]: {
      title: it ? "Ci vuole un po’ più di tempo" : "Taking longer than usual",
      body: it ? "Non riusciamo a confermare l'invio. Resta su questa pagina e riprova tra poco." : "We couldn't confirm delivery. Stay on this page and try again in a moment.",
      short: it ? "Ci vuole un po’ più di tempo. Riprova tra poco." : "This is taking longer than usual. Please try again in a moment.",
    },
    [ERROR_KIND.TEMPORARY]: {
      title: it ? "Riproviamo tra poco" : "Let's try again in a moment",
      body: it ? "Non siamo riusciti a completare la richiesta. Riprova tra qualche secondo." : "We couldn't complete that just yet. Please try again in a few seconds.",
      short: it ? "Riprova tra qualche secondo." : "Please try again in a few seconds.",
    },
    missing_file: {
      short: it ? "Aggiungi la foto e riprova." : "Please add the photo and try again.",
    },
    invalid_image: {
      short: it ? "Scegli un’immagine che si possa aprire e riprova." : "Please choose an image that opens correctly and try again.",
    },
    selfie_image: {
      short: it ? "Scegli un’immagine per il selfie e riprova." : "Please choose an image for your selfie and try again.",
    },
    missing_fields: {
      short: it ? "Completa i campi mancanti e riprova." : "Please fill in the missing details and try again.",
    },
    invalid_fields: {
      short: it ? "Controlla i dati inseriti e riprova." : "Please check your details and try again.",
    },
    [ERROR_KIND.FILE_TOO_LARGE]: {
      title: it ? "Foto troppo grandi" : "Photos are too large",
      body: it
        ? "Prova con foto più leggere o scatta di nuovo con meno zoom."
        : "Try smaller photos or retake them with less zoom.",
      short: it
        ? "Le foto sono troppo grandi. Usa immagini più leggere e riprova."
        : "Your photos are too large. Use smaller images and try again.",
    },
    reservation_not_found: {
      title: it ? "Prenotazione non trovata" : "Reservation not found",
      body: it
        ? "Controlla il link nel messaggio di conferma o chiedi alla struttura il link corretto."
        : "Double-check the link in your confirmation message or ask your host for the correct one.",
    },
    agreement_load: {
      short: it
        ? "Non siamo riusciti a caricare il contratto. Aggiorna la pagina o riprova."
        : "We couldn't load the rental agreement. Refresh the page or try again.",
    },
    selfie: {
      short: it
        ? "Non riusciamo a vedere chiaramente il volto. Scatta un selfie con buona luce."
        : "We couldn't see your face clearly. Take a selfie in good light.",
    },
    verification_fallback: {
      short: it
        ? "Non siamo riusciti a verificare le foto. Usa un selfie nitido e una foto chiara del documento."
        : "We couldn't verify your photos. Use a clear selfie and a sharp photo of your ID.",
    },
    extraction_failed: {
      short: it
        ? "Non siamo riusciti a leggere i dati del documento. Prova con una foto più nitida e ben illuminata."
        : "We couldn't read the details on your document. Try a sharper, well-lit photo.",
    },
    submit_failed: {
      short: it
        ? "Invio non riuscito. Controlla i campi e riprova."
        : "We couldn't send your information. Check the fields and try again.",
    },
  };

  return copies[kind] || copies[ERROR_KIND.TEMPORARY];
}

export function getRequestErrorMessage(err, lang = "en") {
  const kind = classifyAxiosError(err);
  const { code, field } = parseApiErrorPayload(err?.response?.data);
  if (kind === ERROR_KIND.VALIDATION) {
    const copy = field === "selfie" && ["missing_file", "invalid_image"].includes(code)
      ? "selfie_image" : code;
    return getLocalizedErrorCopy(copy, lang).short;
  }
  if (kind === ERROR_KIND.NOT_FOUND) return getLocalizedErrorCopy("reservation_not_found", lang).body;
  return getLocalizedErrorCopy(kind, lang).short;
}

export function getUploadErrorMessage(err, lang = "en") {
  return getRequestErrorMessage(err, lang, "upload");
}

export function getSubmitErrorMessage(err, lang = "en") {
  return getRequestErrorMessage(err, lang, "submit");
}

export function classifyFetchFailure(err) {
  const kind = classifyAxiosError(err);
  if (
    kind === ERROR_KIND.OFFLINE ||
    kind === ERROR_KIND.NETWORK ||
    kind === ERROR_KIND.TIMEOUT
  ) {
    return kind;
  }
  return ERROR_KIND.TEMPORARY;
}

export function buildProblemPageState({ reason, retryPath }) {
  return { state: { reason, retryPath } };
}
